import type { PairReport, StandingsReport } from "../api.js";
import { PRIMARY_CLASS, SORTED_DESC, ranked, rankText } from "../statsPresentation.js";

/**
 * The live standings table — FR-STAT-1, AC-27f.
 *
 * Three requirements shape the markup rather than the styling:
 *
 * AC-27f asks for Wins, Bonus and Penalties beside Match Points, so that
 * **`MP = Wins + Bonus − Penalties` holds visibly for every row**. BR-STAT-0a
 * explains why: a single aggregate MP hides whether somebody is winning often
 * or winning heavily, which is exactly the distinction the club's scoring
 * system was built to draw — and it lets any member check the arithmetic
 * without drilling into individual games.
 *
 * The legacy sheet's points columns are here too — points won and points lost
 * — along with both dominance figures. Dom is points per game; Dom% is the
 * same thing with each game divided by its own target, which is the only fair
 * way to compare a 15-point game with a 21. The club mixes both in one
 * session, so the raw figure on its own flatters whoever played the longer
 * games.
 *
 * What is **not** here is anything the reader can do in their head from a
 * column that is: losses are games minus wins, and point difference is points
 * won minus points lost. Thirteen columns on a phone meant swiping past
 * arithmetic to reach figures that say something new, so the two derivable
 * ones are gone.
 *
 * The same criterion asks for the player column to stay visible while the
 * table scrolls sideways. A row of numbers with the name scrolled off is worse
 * than useless.
 *
 * BR-STAT-1 — a value with no denominator renders as an em dash. Never a zero,
 * which would read as a real result, and never an error.
 */
export function Standings({
  report,
  pairs,
  onSwitchType,
}: {
  report: StandingsReport;
  pairs?: PairReport;
  onSwitchType?: (gameType: "doubles" | "singles") => void;
}): React.JSX.Element {
  /*
   * The switch is rendered before anything branches on whether there are rows.
   *
   * It used to sit inside the populated branch, so switching to a game type
   * with no results replaced the whole card — switch included — with "No games
   * logged yet." The club had no singles games, tapped singles, and had no way
   * back to doubles. An empty state that removes the control which caused it is
   * a trap, and this one had no exit at all.
   */
  const switcher = onSwitchType ? (
    <div className="row">
      {(["doubles", "singles"] as const).map((t) => (
        <button
          key={t}
          className={t === report.gameType ? "chip chip-on" : "chip"}
          onClick={() => onSwitchType(t)}
        >
          {t}
        </button>
      ))}
    </div>
  ) : null;

  const heading = (
    <div className="row space-between">
      <h3 className="card-head">
        Standings
        {/* BR-COMMIT-2 — said, not implied. */}
        {report.provisional ? <span className="provisional-flag">provisional</span> : null}
      </h3>
      {switcher}
    </div>
  );

  if (report.rows.length === 0) {
    return (
      <section className="block">
        {heading}
        <p>
          No {report.gameType} games logged yet.
        </p>
      </section>
    );
  }

  return (
    <section className="block">
      {heading}

      <p className="swipehint">Swipe the table sideways for more columns</p>

      <div className="table-scroll">
        <table className="standings">
          <thead>
            <tr>
              {/* FR-STAT-17 — rows arrive from the server already in rank order,
                  so the number is reported rather than derived from the index:
                  tied players share a position and the one after them skips. */}
              <th scope="col" className="rank" aria-label="Rank">
                #
              </th>
              <th scope="col" className="sticky-col">
                Player
              </th>
              <th scope="col">G</th>
              <th scope="col">W</th>
              <th scope="col">Win%</th>
              <th scope="col">PW</th>
              <th scope="col">PL</th>
              <th scope="col">Dom</th>
              <th scope="col">Dom%</th>
              {/* The decomposition AC-27f requires, in the order the identity
                  reads: Wins + Bonus − Penalties = MP. */}
              <th scope="col">Bon</th>
              <th scope="col">Pen</th>
              <th scope="col" className={PRIMARY_CLASS} aria-sort="descending">
                MP<span className="sortmark">{SORTED_DESC}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r) => (
              <tr key={r.playerId} className={report.leaders.includes(r.playerId) ? "leader" : undefined}>
                <td className="rank">{rankText(r.rank, r.shared)}</td>
                <th scope="row" className="sticky-col">
                  {r.displayName}
                  {r.isGuest ? <span className="guest-flag">guest</span> : null}
                  {r.shared && r.rank === 1 ? <span className="provisional-flag">joint</span> : null}
                </th>
                <td>{r.games}</td>
                <td>{r.wins}</td>
                <td>{pct(r.winPct)}</td>
                <td>{r.pointsWon}</td>
                <td>{r.pointsLost}</td>
                <td className={sign(r.dominance)}>{dec(r.dominance)}</td>
                <td className={sign(r.normalisedDominance)}>{pct(r.normalisedDominance)}</td>
                <td>{r.bonus}</td>
                <td>{r.penalties}</td>
                <td className={PRIMARY_CLASS}>{r.matchPoints}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="note">
        MP = Wins + Bonus − Penalties. Dom is points per game; Dom% divides each
        game by its own target, so a 15 and a 21 compare fairly.
      </p>

      {/* BR-PAIR-2 — doubles only, and only alongside the doubles table: a
          singles standings with a pair table under it would be nonsense. */}
      {pairs && report.gameType === "doubles" ? <PairTable pairs={pairs} /> : null}
    </section>
  );
}

/**
 * Who wins with whom — BR-PAIR-1, BR-PAIR-2, FR-STAT-6.
 *
 * BR-PAIR-4 sets the minimum at three games together before a partnership is
 * worth a claim. Pairs below it are still listed, marked, because in a session
 * of a dozen games most partnerships will be below it and an empty table would
 * say the feature was broken. What the minimum changes is how a row reads, not
 * whether it exists — the same principle as BR-PAIR-5.
 */
function PairTable({ pairs }: { pairs: PairReport }): React.JSX.Element | null {
  if (pairs.rows.length === 0) return null;

  return (
    <section className="block">
      <h3 className="card-head">Pairs</h3>
      <div className="table-scroll">
        <table className="standings">
          <thead>
            <tr>
              <th scope="col" className="rank" aria-label="Rank">
                #
              </th>
              <th scope="col" className="sticky-col">
                Partnership
              </th>
              <th scope="col">G</th>
              <th scope="col">W</th>
              <th scope="col">Win%</th>
              <th scope="col">PW</th>
              <th scope="col">PL</th>
              <th scope="col">Dom</th>
              <th scope="col" className={PRIMARY_CLASS} aria-sort="descending">
                MP<span className="sortmark">{SORTED_DESC}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Ranked here rather than trusted from the server, because pair
                rows carry no rank of their own. */}
            {ranked(pairs.rows).map((p) => (
              <tr key={p.pairKey} className={p.rank === 1 ? "leader" : undefined}>
                <td className="rank">{rankText(p.rank, p.shared)}</td>
                <th scope="row" className="sticky-col">
                  {p.label}
                  {p.meetsMinimum ? null : (
                    <span className="provisional-flag">
                      {p.games} of {pairs.minimumGames}
                    </span>
                  )}
                </th>
                <td>{p.games}</td>
                <td>{p.wins}</td>
                <td>{pct(p.winPct)}</td>
                <td>{p.pointsWon}</td>
                <td>{p.pointsLost}</td>
                <td className={sign(p.dominance)}>{dec(p.dominance)}</td>
                <td className={PRIMARY_CLASS}>{p.matchPoints}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        A partnership needs {pairs.minimumGames} games together before it counts
        as a record rather than a coincidence. The rest are shown anyway, marked.
      </p>
    </section>
  );
}

/** BR-STAT-1 — an em dash where there is nothing to divide by. */
function pct(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function dec(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

function sign(value: number | null): string | undefined {
  if (value === null || value === 0) return undefined;
  return value > 0 ? "pos" : "neg";
}
